import {
  Avatar,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link as ChakraLink,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import QRCode from "qrcode.react";
import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../components/student/StudentPortalShell";
import DailyChallengeMenu from "../../components/student/DailyChallengeMenu";
import MissedQuestions from "../../components/student/Cards/MissedQuestions";
import RecentContentPanel from "../../components/student/RecentContentPanel";
import UpcomingOfficeHoursPanel from "../../components/student/UpcomingOfficeHoursPanel";
import LiveSessionsPanel from "../../components/student/LiveSessionsPanel";
import HomeQuickLinksPanel from "../../components/student/HomeQuickLinksPanel";
import { getAllExamsAndHomeworksRequest } from "../../modules/students-modules/exams/Actions";
import { getLiveVideoRequest } from "../../modules/students-modules/videos/Actions";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";

const C = {
  bg: studentSurfaces.canvas,
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  paper: studentSurfaces.raised,
  warmBeige: studentPalette.warmBeige,
  muted: studentSurfaces.textMuted,
};

function scanAttendanceUrl(userId) {
  const raw = "https://edutopiaonline.com/";
  const base = String(raw)
    .replace(/\s/g, "")
    .replace(/['"]/g, "")
    .replace(/\/$/, "");
  return `${base}/scan-code/${userId}/attendence`;
}

const StudentHome = ({ t }) => {
  const baseUrl = "https://api.edutopiaonline.com/";
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const liveVideo = useSelector((state) => state.chapterVideos.liveVideo);
  const performanceData = useSelector((state) => state.performance.analytics);
  const perfLoading = useSelector((state) => state.performance.isLoading);

  const { isOpen: isChallengeOpen, onOpen: onChallengeOpen, onClose: onChallengeClose } = useDisclosure();

  useEffect(() => {
    dispatch(getLiveVideoRequest());
    dispatch(getAllExamsAndHomeworksRequest());
  }, [dispatch]);

  return (
    <StudentPortalShell>
      <Box
        bg={C.bg}
        minH={{ base: "auto", md: "100vh" }}
        py={{ base: 6, md: 10 }}
        px={{ base: 4, md: 6, lg: 8 }}
        overflowX="hidden"
        w="100%"
        maxW="100%"
        minW={0}
      >
        <Box w="100%" maxW="100%" minW={0}>
          {/* Top: dossier + recent */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={8} w="full" minW={0}>
            <GridItem minW={0}>
              <Flex
                position="relative"
                direction={{ base: "column", sm: "row" }}
                align={{ base: "stretch", sm: "center" }}
                justify="space-between"
                gap={6}
                p={{ base: 6, md: 8 }}
                bg={C.paper}
                borderWidth="1px"
                borderColor={C.border}
                borderRadius="lg"
                boxShadow="sm"
                w="full"
                minW={0}
                h="full"
              >
                {userData?.grade?.id === 10 && (
                  <Box position="absolute" top="-10px" left="-10px" zIndex={1}>
                    <Text
                      bg="yellow.400"
                      color="white"
                      fontWeight="800"
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      letterSpacing="0.1em"
                      textTransform="uppercase"
                      boxShadow="md"
                    >
                      Premium
                    </Text>
                  </Box>
                )}
                <Flex align="flex-start" gap={{ base: 4, md: 8 }} flex="1" minW={0}>
                  <Avatar
                    size="xl"
                    borderRadius="lg"
                    src={userData?.subject?.logo ? baseUrl + userData.subject.logo.replace(/^\//, "") : undefined}
                    name={userData?.name}
                  />
                  <Box minW={0}>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.2em"
                      color={`${C.secondary}99`}
                      textTransform="uppercase"
                      mb={1}
                    >
                      Student dossier
                    </Text>
                    <Heading
                      size="lg"
                      color={C.primary}
                      fontWeight="800"
                      mb={4}
                      fontFamily="'Tajawal','Amiri',sans-serif"
                      maxW="100%"
                      wordBreak="break-word"
                      sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                    >
                      {userData?.name}
                    </Heading>
                    <Flex gap={10} flexWrap="wrap">
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Group
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.group?.name || "—"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Subject
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.subject?.name || "—"}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <VStack
                  spacing={2}
                  p={4}
                  bg={studentSurfaces.muted}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  align="center"
                  minW={{ base: "full", sm: "140px" }}
                  w={{ base: "full", sm: "auto" }}
                >
                  <Box
                    bg={C.paper}
                    p={2}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={C.border}
                  >
                    <QRCode size={112} value={scanAttendanceUrl(userData?.id)} />
                  </Box>
                  <Text fontSize="9px" fontWeight="800" color={`${C.secondary}99`} textTransform="uppercase" letterSpacing="widest">
                    Attendance QR
                  </Text>
                </VStack>
              </Flex>
            </GridItem>

            <GridItem minW={0} display="flex" flexDirection="column">
              <RecentContentPanel performanceData={performanceData} isLoading={perfLoading} />
            </GridItem>
          </Grid>

          {/* Four home cards */}
          <Grid
            templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
            gap={6}
            mb={10}
            w="full"
            minW={0}
            alignItems="stretch"
          >
            <GridItem minW={0} display="flex">
              <LiveSessionsPanel
                sessions={Array.isArray(liveVideo) ? liveVideo : []}
              />
            </GridItem>
            <GridItem minW={0} display="flex">
              <HomeQuickLinksPanel type="exams" />
            </GridItem>
            <GridItem minW={0} display="flex">
              <HomeQuickLinksPanel type="homework" />
            </GridItem>
            <GridItem minW={0} display="flex">
              <UpcomingOfficeHoursPanel />
            </GridItem>
          </Grid>

          <Box mb={10} w="full" minW={0}>
            <MissedQuestions />
          </Box>

          <Flex
            pt={8}
            pb={4}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={6}
            align={{ base: "flex-start", md: "flex-end" }}
          >
            <Box maxW="md">
              <Text fontSize="10px" fontWeight="800" letterSpacing="0.12em" color={`${C.secondary}66`} textTransform="uppercase" mb={2}>
                Edutopia student portal
              </Text>
              <Text fontSize="sm" color={C.muted} lineHeight="tall">
                Your progress and attendance tools in one place.
              </Text>
            </Box>
            <ChakraLink
              as={Link}
              to="/privacy-policy"
              fontSize="9px"
              fontWeight="800"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={C.secondary}
              _hover={{ color: C.primary }}
            >
              Privacy
            </ChakraLink>
          </Flex>
        </Box>
      </Box>

      <DailyChallengeMenu isOpen={isChallengeOpen} onClose={onChallengeClose} userData={userData} />
    </StudentPortalShell>
  );
};

export default withTranslation()(StudentHome);
